import { queryOptions, useMutation, useQueryClient, InfiniteData, QueryClient } from '@tanstack/react-query';
import { makeRequest, queryKey, PickerItem } from '@easylife365/react';
import { httpDelete, HttpError, httpGet, httpPatch, httpPost, httpPut, toDownloadedFile, toJson } from './http';
import {
  AddPasswordRequest,
  AddPasswordResponse,
  AppLastSignInDto,
  AppSettingsDto,
  GenerateSamlCertificateRequest,
  GenerateSamlCertificateResponse,
  IdentityTaskDto,
  TaskAssigneesDto,
  IsAppEnabledResponse,
  AppPolicyBundle,
  ResourceType,
  UpdateKeyCredentialsRequest,
  UpdateKeyCredentialsResponse,
  UploadSamlCertificateRequest,
  UploadSamlCertificateResponse,
  UpsertAppSettingsDto,
  UpsertTaskRequest,
  UserSettingsDto,
  ActivateSamlCertificateRequest,
  RemovePasswordRequest,
  UpdateSamlCertificateResponse,
  AddApplicationOwnerRequest,
  RemoveOwnerRequest,
  IsAppOnboardedResponse,
  IsAppOnboardedRequest,
  AddSecondaryOwnerRequest,
  PolicyExtension,
  UpdateAppPropertiesRequest,
  PermissionSet,
} from './app.types';
import { graphQueryKeys } from './graph.api';
import { AppBaseDto, GraphListResponse } from './graph.types';
import { updateCredentials } from '../services/utils';
import { Auth } from '@easylife365/react/auth';
import { sameId } from '../lib/ids';

const baseUrl = import.meta.env.VITE_EASY_APIENDPOINT as string;
const apiScopes = [import.meta.env.VITE_EASY_EASYLIFESCOPE as string];

export const appQueryKeys = {
  isAppEnabled: queryKey<IsAppEnabledResponse>('app', 'isAppEnabled'),
  getAppSettings: (id: string) => queryKey<AppSettingsDto>('apps', id, 'settings'),
  getMySettings: queryKey<UserSettingsDto>('app', 'mySettings'),
  getAppsLastSignIn: queryKey<AppLastSignInDto[]>('apps', 'lastSignIn'),
  getTasks: queryKey<IdentityTaskDto[]>('app', 'tasks'),
  getTask: (id: string) => queryKey<IdentityTaskDto>('app', 'tasks', id),
  getTaskAssignees: (appId: string, resourceType: ResourceType) =>
    queryKey<TaskAssigneesDto>('app', 'tasks', 'assignees', appId, resourceType),
  getPermissions: (id: string) => queryKey<PermissionSet>('app', 'permissions', id),
  getPolicyBundle: (id: string) => queryKey<AppPolicyBundle>('app', 'policies', id),
};

export const appQueries = {
  isAppEnabled: queryOptions({
    queryKey: appQueryKeys.isAppEnabled,
    queryFn: ({ signal }) =>
      makeRequest(baseUrl, apiScopes, 'isAppEnabled', httpGet(signal), toJson<IsAppEnabledResponse>),
  }),
  getTasks: queryOptions({
    queryKey: appQueryKeys.getTasks,
    queryFn: async ({ signal }) =>
      await makeRequest(baseUrl, apiScopes, 'tasks', httpGet(signal), toJson<IdentityTaskDto[]>),
  }),
  getTask: (id: string) =>
    queryOptions({
      queryKey: appQueryKeys.getTask(id),
      queryFn: async ({ signal }) =>
        await makeRequest(baseUrl, apiScopes, `tasks/${id}`, httpGet(signal), toJson<IdentityTaskDto>),
    }),
  getAppSettings: (id: string) =>
    queryOptions({
      queryKey: appQueryKeys.getAppSettings(id),
      queryFn: async ({ signal }) =>
        await makeRequest(baseUrl, apiScopes, `apps/${id}/settings`, httpGet(signal), toJson<AppSettingsDto>),
    }),
  // One request for the whole overview table; the per-app settings call would be one request per row.
  getAppsLastSignIn: queryOptions({
    queryKey: appQueryKeys.getAppsLastSignIn,
    // Only the daily-ish activity scan writes these, and the call reads the tenant's whole settings table.
    staleTime: 10 * 60 * 1000,
    queryFn: async ({ signal }) =>
      await makeRequest(baseUrl, apiScopes, 'apps/lastSignIn', httpGet(signal), toJson<AppLastSignInDto[]>),
  }),
  getMySettings: queryOptions({
    queryKey: appQueryKeys.getMySettings,
    queryFn: async ({ signal }) =>
      await makeRequest(baseUrl, apiScopes, 'me/settings', httpGet(signal), toJson<UserSettingsDto>),
  }),
  // Who a task of the app can be assigned to. The list is short (owners and administrators of the app), so the picker
  // reads it once and searches it locally.
  getTaskAssignees: (appType: ResourceType, appId: string) =>
    queryOptions({
      queryKey: appQueryKeys.getTaskAssignees(appId, appType),
      queryFn: async ({ signal }) =>
        await makeRequest(
          baseUrl,
          apiScopes,
          `tasks/assignees?appObjectId=${encodeURIComponent(appId)}&resourceType=${appType}`,
          httpGet(signal),
          toJson<TaskAssigneesDto>
        ),
    }),
  getPermissions: (appType: ResourceType, id: string) =>
    queryOptions({
      queryKey: appQueryKeys.getPermissions(id), // appType is not included in query because the id alone is enough to identify the resource
      queryFn: async ({ signal }) =>
        await makeRequest(baseUrl, apiScopes, `permissions/${appType}/${id}`, httpGet(signal), toJson<PermissionSet>),
    }),
  // The bundle governing this app. Resolved server-side, because the app cannot work it out itself: it
  // carries a stamped bundle only once the scan has reached it, and until then the tenant default governs.
  getPolicyBundle: (appType: ResourceType, id: string) =>
    queryOptions({
      queryKey: appQueryKeys.getPolicyBundle(id), // appType is not included in query because the id alone is enough to identify the resource
      queryFn: async ({ signal }) => {
        try {
          return await makeRequest(
            baseUrl,
            apiScopes,
            `policies/${appType}/${id}`,
            httpGet(signal),
            toJson<AppPolicyBundle>
          );
        } catch (error) {
          // nothing governs the app: no default is configured, or the bundle stamped on it is inactive
          if (error instanceof HttpError && error.statusCode === 404) return null;

          throw error;
        }
      },
    }),
};

export type IsAppOnboardedVariables = {
  request: IsAppOnboardedRequest;
};

export function useIsAppOnboarded() {
  return useMutation({
    mutationKey: ['app', 'isAppOnboarded'],
    mutationFn: ({ request }: IsAppOnboardedVariables) =>
      makeRequest(baseUrl, apiScopes, 'isOnboarded', httpPost(request), toJson<IsAppOnboardedResponse>),
  });
}

export type UpsertTaskVariables = {
  id: string;
  request: UpsertTaskRequest;
};

export type UseUpsertTaskParameters = {
  onSuccess?: () => void;
};

export function useUpsertTask({ onSuccess }: UseUpsertTaskParameters = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'tasks', 'upsert'],
    mutationFn: ({ id, request }: UpsertTaskVariables) =>
      makeRequest(baseUrl, apiScopes, `tasks/${id}`, httpPatch(request), toJson<IdentityTaskDto>),
    async onSuccess(task, { id }) {
      // The panel fills its form from the single task and sends every field back on save, so this entry must never
      // lag behind the list. It is keyed by the id the panel asked with, whatever the casing of the one that came back.
      // A fetch still running from before the save would otherwise land after this write and put the old task back.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: appQueryKeys.getTask(id) }),
        queryClient.cancelQueries({ queryKey: appQueryKeys.getTasks, exact: true }),
      ]);
      queryClient.setQueryData(appQueryKeys.getTask(id), task);
      queryClient.setQueryData(appQueryKeys.getTasks, (tasks) => {
        if (!tasks) return [task];

        const index = tasks.findIndex((t) => sameId(t.id, task.id));
        return index === -1 ? [...tasks, task] : tasks.toSpliced(index, 1, task);
      });
      onSuccess?.();
    },
  });
}

export type UpsertAppSettingsVariables = {
  id: string;
  request: UpsertAppSettingsDto;
};

export function useUpsertAppSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'settings', 'upsert'],
    mutationFn: (variables: UpsertAppSettingsVariables) =>
      makeRequest(
        baseUrl,
        apiScopes,
        `apps/${variables.id}/settings`,
        httpPut(variables.request),
        toJson<AppSettingsDto>
      ),
    async onSuccess(data, variables) {
      // The form takes its settings from this entry. A settings request that was already running when the user saved
      // would otherwise land after this write and put the old settings back until the next refetch.
      // The same goes for the enabled-state entry patched below.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: appQueryKeys.getAppSettings(variables.id) }),
        queryClient.cancelQueries({ queryKey: appQueryKeys.isAppEnabled }),
      ]);
      queryClient.setQueryData(appQueryKeys.getAppSettings(variables.id), data);

      queryClient.setQueryData(appQueryKeys.isAppEnabled, (isAppEnabledResponse) => {
        if (!isAppEnabledResponse || data.isManaged === undefined) return isAppEnabledResponse;

        return data.isManaged
          ? {
              ...isAppEnabledResponse,
              managedAppInfo: {
                managedAppIds: isAppEnabledResponse.managedAppInfo.managedAppIds.includes(variables.id)
                  ? isAppEnabledResponse.managedAppInfo.managedAppIds
                  : [...isAppEnabledResponse.managedAppInfo.managedAppIds, variables.id],
                unmanagedAppIds: isAppEnabledResponse.managedAppInfo.unmanagedAppIds.filter(
                  (appId) => appId !== variables.id
                ),
                managedAppOwnerIds: isAppEnabledResponse.managedAppInfo.managedAppOwnerIds,
              },
            }
          : {
              ...isAppEnabledResponse,
              managedAppInfo: {
                managedAppIds: isAppEnabledResponse.managedAppInfo.managedAppIds.filter(
                  (appId) => appId !== variables.id
                ),
                unmanagedAppIds: isAppEnabledResponse.managedAppInfo.unmanagedAppIds.includes(variables.id)
                  ? isAppEnabledResponse.managedAppInfo.unmanagedAppIds
                  : [...isAppEnabledResponse.managedAppInfo.unmanagedAppIds, variables.id],
                managedAppOwnerIds: isAppEnabledResponse.managedAppInfo.managedAppOwnerIds,
              },
            };
      });
    },
  });
}

export type AddPasswordVariables = {
  id: string;
  request: AddPasswordRequest;
};

export function useAddPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'passwords', 'add'],
    mutationFn: ({ id, request }: AddPasswordVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/addPassword`, httpPost(request), toJson<AddPasswordResponse>),
    // The response carries the plaintext secret; drop the finished mutation as soon as nothing observes it.
    gcTime: 0,
    // Not idempotent: a retry after a 5xx could create further secrets that are never shown.
    retry: false,
    async onSuccess(data, variables) {
      // The secret is shown once, in the panel. Keep it out of the cached app the rest of the page reads.
      const { secretText: _secretText, ...password } = data.password;
      await cancelAppQueries(queryClient, variables.request.appType, variables.id, { paged: false });
      queryClient.setQueryData(
        graphQueryKeys.getResource(variables.request.appType, variables.id),
        (app: AppBaseDto | undefined) => {
          if (!app) return;

          return {
            ...app,
            passwordCredentials: [...app.passwordCredentials, password],
          };
        }
      );
    },
  });
}

export type UpdateKeyCredentialsVariables = {
  id: string;
  request: UpdateKeyCredentialsRequest;
};

export function useUpdateKeyCredentials(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'certificates', 'update'],
    mutationFn: ({ id, request }: UpdateKeyCredentialsVariables) =>
      makeRequest(
        baseUrl,
        apiScopes,
        `apps/${id}/updateKeyCredentials`,
        httpPost(request),
        toJson<UpdateKeyCredentialsResponse>
      ),
    async onSuccess(data, variables) {
      const queryKey = graphQueryKeys.getResource(resourceType, variables.id);

      const app = queryClient.getQueryData(queryKey);
      if (!app) return;

      const updatedApp = {
        ...app,
        keyCredentials: data.keyCredentials,
        passwordCredentials: data.passwordCredentials,
      };

      await updateCredentials(updatedApp);

      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: true });
      queryClient.setQueryData(queryKey, (current) => current && withCredentialsFrom(current, updatedApp));
      updateAppInPagedQueries(queryClient, resourceType, variables.id, (listed) =>
        withCredentialsOf(listed, updatedApp)
      );
    },
  });
}

export type UploadSamlCertificateVariables = {
  id: string;
  request: UploadSamlCertificateRequest;
};

export function useUploadSamlCertificate(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'samlCertificates', 'update'],
    mutationFn: ({ id, request }: UploadSamlCertificateVariables) =>
      makeRequest(
        baseUrl,
        apiScopes,
        `servicePrincipals/${id}/uploadSamlCertificate`,
        httpPost(request),
        toJson<UploadSamlCertificateResponse>
      ),
    async onSuccess(data, variables) {
      const queryKey = graphQueryKeys.getResource(resourceType, variables.id);

      const app = queryClient.getQueryData(queryKey);
      if (!app) return;

      const updatedApp = {
        ...app,
        keyCredentials: data.keyCredentials,
        passwordCredentials: data.passwordCredentials,
      };

      await updateCredentials(updatedApp);

      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: true });
      queryClient.setQueryData(queryKey, (current) => current && withCredentialsFrom(current, updatedApp));
      updateAppInPagedQueries(queryClient, resourceType, variables.id, (listed) =>
        withCredentialsOf(listed, updatedApp)
      );
    },
  });
}

export type GenerateSamlCertificateVariables = {
  id: string;
  request: GenerateSamlCertificateRequest;
};

export function useGenerateSamlCertificate(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'samlCertificates', 'generate'],
    mutationFn: ({ id, request }: GenerateSamlCertificateVariables) =>
      makeRequest(
        baseUrl,
        apiScopes,
        `servicePrincipals/${id}/generateSamlCertificate`,
        httpPost(request),
        toJson<GenerateSamlCertificateResponse>
      ),
    async onSuccess(data, variables) {
      const queryKey = graphQueryKeys.getResource(resourceType, variables.id);

      const app = queryClient.getQueryData(queryKey);
      if (!app) return;

      const updatedApp = {
        ...app,
        keyCredentials: [...app.keyCredentials, data.certificate],
      };

      await updateCredentials(updatedApp);

      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: true });
      queryClient.setQueryData(queryKey, (current) => current && withCredentialsFrom(current, updatedApp));
      updateAppInPagedQueries(queryClient, resourceType, variables.id, (listed) =>
        withCredentialsOf(listed, updatedApp)
      );
    },
  });
}

export type ActivateSamlCertificateVariables = {
  id: string;
  request: ActivateSamlCertificateRequest;
};

export function useActivateSamlCertificate(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'samlCertificates', 'activate'],
    mutationFn: ({ id, request }: ActivateSamlCertificateVariables) =>
      makeRequest(baseUrl, apiScopes, `servicePrincipals/${id}/activateSamlCertificate`, httpPost(request)),
    async onSuccess(_data, variables) {
      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: false });
      queryClient.setQueryData(
        graphQueryKeys.getResource(resourceType, variables.id),
        (app) =>
          app && {
            ...app,
            preferredTokenSigningKeyThumbprint: variables.request.thumbprint,
          }
      );
    },
  });
}

export type RemovePasswordVariables = {
  id: string;
  request: RemovePasswordRequest;
};

export function useRemovePassword(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'passwords', 'remove'],
    mutationFn: ({ id, request }: RemovePasswordVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/removePassword`, httpPost(request)),
    async onSuccess(_data, variables) {
      const updater = (app: AppBaseDto) => ({
        ...app,
        passwordCredentials: app.passwordCredentials.filter((secret) => secret.keyId !== variables.request.keyId),
      });

      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: true });
      queryClient.setQueryData(graphQueryKeys.getResource(resourceType, variables.id), (app) => app && updater(app));
      updateAppInPagedQueries(queryClient, resourceType, variables.id, updater);
    },
  });
}

export type DownloadCertificateVariables = {
  id: string;
  resourceType: ResourceType;
  certificateId: string;
  format?: 'base64' | 'pem' | 'raw' | 'xml';
};

export function useDownloadCertificate() {
  return useMutation({
    mutationKey: ['app', 'certificates', 'download'],
    mutationFn: ({ id, resourceType, certificateId, format }: DownloadCertificateVariables) =>
      makeRequest(
        baseUrl,
        apiScopes,
        `apps/${resourceType}/${id}/downloadCertificate/${certificateId}${format ? `?format=${format}` : ''}`,
        httpGet(),
        toDownloadedFile
      ),
  });
}

export type DeleteSamlCertificateVariables = {
  id: string;
  certificateId: string;
};

export function useDeleteSamlCertificate(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'samlCertificates', 'delete'],
    mutationFn: ({ id, certificateId }: DeleteSamlCertificateVariables) =>
      makeRequest(
        baseUrl,
        apiScopes,
        `servicePrincipals/${id}/deleteSamlCertificate/${certificateId}`,
        httpDelete(),
        toJson<UpdateSamlCertificateResponse>
      ),
    async onSuccess(data, variables) {
      const queryKey = graphQueryKeys.getResource(resourceType, variables.id);
      const app = queryClient.getQueryData(queryKey);
      if (!app) return;

      const updatedApp = {
        ...app,
        keyCredentials: data.keyCredentials,
        passwordCredentials: data.passwordCredentials,
      };

      await updateCredentials(updatedApp);

      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: true });
      queryClient.setQueryData(queryKey, (current) => current && withCredentialsFrom(current, updatedApp));
      updateAppInPagedQueries(queryClient, resourceType, variables.id, (listed) =>
        withCredentialsOf(listed, updatedApp)
      );
    },
  });
}

export type AddApplicationOwnerVariables = {
  id: string;
  pickerItem: PickerItem;
};

export function useAddApplicationOwner(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'owners', 'add'],
    mutationFn: ({ id, pickerItem }: AddApplicationOwnerVariables) =>
      makeRequest(
        baseUrl,
        apiScopes,
        `apps/${id}/addApplicationOwner`,
        httpPost<AddApplicationOwnerRequest>({ appType: resourceType, userId: pickerItem.id! })
      ),
    async onSuccess(_data, variables) {
      const pickerItem = variables.pickerItem;
      const resourceKey = graphQueryKeys.getResource(resourceType, variables.id);

      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: false });
      queryClient.setQueryData(resourceKey, (app) => {
        if (!app) return app;
        // The owner can already be listed when the request is repeated or a refetch brought it in.
        if (app.owners.some((owner) => sameId(owner.id, pickerItem.id))) return app;

        return {
          ...app,
          owners: app.owners.toSpliced(app.owners.length - 1, 0, {
            id: pickerItem.id!,
            displayName: pickerItem.displayName ?? null,
            mail: pickerItem.mail ?? null,
            userPrincipalName: pickerItem.userPrincipalName,
          }),
        };
      });

      if (sameId(Auth.getInstance().getUserAccount()?.id, variables.pickerItem.id)) {
        queryClient.invalidateQueries({
          queryKey: appQueryKeys.getPermissions(variables.id),
        });
      }
    },
  });
}

export type RemoveOwnerVariables = {
  id: string;
  request: RemoveOwnerRequest;
};

export function useRemoveOwner(resourceType: ResourceType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'owners', 'remove'],
    mutationFn: ({ id, request }: RemoveOwnerVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/removeOwner`, httpPost(request)),
    async onSuccess(_data, variables) {
      await cancelAppQueries(queryClient, resourceType, variables.id, { paged: false });
      queryClient.setQueryData(
        graphQueryKeys.getResource(resourceType, variables.id),
        (app) =>
          app && {
            ...app,
            owners: app.owners.filter((owner) => !sameId(owner.id, variables.request.userId)),
          }
      );

      if (sameId(Auth.getInstance().getUserAccount()?.id, variables.request.userId)) {
        queryClient.invalidateQueries({
          queryKey: appQueryKeys.getPermissions(variables.id),
        });

        await cancelQueriesWithData(queryClient, [getMyResourcesQueryKey(resourceType)]);
        queryClient.setQueryData(getMyResourcesQueryKey(resourceType), (data) => {
          if (!data) return;

          return {
            pageParams: data.pageParams,
            pages: data.pages.map((page) => {
              if (!page.value) return page;

              return {
                '@odata.nextLink': page['@odata.nextLink'],
                value: page.value.filter((app) => !sameId(app.id, variables.id)),
              };
            }),
          };
        });
      }
    },
  });
}

function getMyResourcesQueryKey(resourceType: ResourceType) {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return graphQueryKeys.getMyAppRegistrations;

    case ResourceType.EnterpriseApplication:
      return graphQueryKeys.getMyEnterpriseApplications;
  }
}

export type UpdateAppPropertiesVariables = {
  id: string;
  request: UpdateAppPropertiesRequest;
};

export function useUpdateAppProperties() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'properties', 'update'],
    mutationFn: ({ id, request }: UpdateAppPropertiesVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/properties`, httpPut(request)),
    async onSuccess(_data, variables) {
      await cancelQueriesWithData(queryClient, [graphQueryKeys.getEnterpriseApplication(variables.id)]);
      queryClient.setQueryData(
        graphQueryKeys.getEnterpriseApplication(variables.id),
        (app) =>
          app && {
            ...app,
            accountEnabled: variables.request.accountEnabled ?? app.accountEnabled,
            appRoleAssignmentRequired: variables.request.appRoleAssignmentRequired ?? app.appRoleAssignmentRequired,
          }
      );
    },
  });
}

export type AddTechnicalOwnerVariables = {
  id: string;
  request: AddSecondaryOwnerRequest;
};

export function useAddTechnicalOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'technicalOwners', 'add'],
    mutationFn: ({ id, request }: AddTechnicalOwnerVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/addTechnicalOwner`, httpPost(request)),
    async onSuccess(_data, variables) {
      const id = variables.id;

      // A settings request that was already running would otherwise answer after this write and put the old owners back.
      await queryClient.cancelQueries({ queryKey: appQueryKeys.getAppSettings(id) });
      queryClient.setQueryData(appQueryKeys.getAppSettings(id), (settings) => {
        if (!settings) return settings;
        // The owner can already be listed when the request is repeated or a refetch brought it in.
        if (settings.technicalOwnershipSettings.owners.some((owner) => sameId(owner.id, variables.request.owner.id))) {
          return settings;
        }

        return {
          ...settings,
          technicalOwnershipSettings: {
            ...settings.technicalOwnershipSettings,
            owners: [...settings.technicalOwnershipSettings.owners, variables.request.owner],
          },
        };
      });

      if (sameId(Auth.getInstance().getUserAccount()?.id, variables.request.owner.id)) {
        queryClient.invalidateQueries({
          queryKey: appQueryKeys.getPermissions(variables.id),
        });

        await queryClient.cancelQueries({ queryKey: appQueryKeys.getMySettings });
        queryClient.setQueryData(appQueryKeys.getMySettings, (mySettings) => {
          if (!mySettings) return undefined;

          return {
            ...mySettings,
            technicalOwnershipSettings: {
              ownerApps: {
                ...mySettings.technicalOwnershipSettings.ownerApps,
                [id]: {
                  appType: variables.request.appType,
                },
              },
            },
          };
        });
      }
    },
  });
}

export type RemoveTechnicalOwnerVariables = {
  id: string;
  technicalOwnerId: string;
};

export function useRemoveTechnicalOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'technicalOwners', 'remove'],
    mutationFn: ({ id, technicalOwnerId }: RemoveTechnicalOwnerVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/removeTechnicalOwner/${technicalOwnerId}`, httpPost()),
    async onSuccess(_data, variables) {
      const id = variables.id;

      // A settings request that was already running would otherwise answer after this write and put the old owners back.
      await queryClient.cancelQueries({ queryKey: appQueryKeys.getAppSettings(id) });
      queryClient.setQueryData(
        appQueryKeys.getAppSettings(id),
        (settings) =>
          settings && {
            ...settings,
            technicalOwnershipSettings: {
              ...settings.technicalOwnershipSettings,
              owners: settings.technicalOwnershipSettings.owners.filter(
                (technicalOwner) => !sameId(technicalOwner.id, variables.technicalOwnerId)
              ),
            },
          }
      );

      if (sameId(Auth.getInstance().getUserAccount()?.id, variables.technicalOwnerId)) {
        queryClient.invalidateQueries({
          queryKey: appQueryKeys.getPermissions(variables.id),
        });

        await queryClient.cancelQueries({ queryKey: appQueryKeys.getMySettings });
        queryClient.setQueryData(appQueryKeys.getMySettings, (mySettings) => {
          if (!mySettings) return undefined;

          if (!(id in mySettings.technicalOwnershipSettings.ownerApps)) return mySettings;

          const { [id]: _removed, ...ownerApps } = mySettings.technicalOwnershipSettings.ownerApps;

          return { ...mySettings, technicalOwnershipSettings: { ...mySettings.technicalOwnershipSettings, ownerApps } };
        });
      }
    },
  });
}

export type AddBusinessOwnerVariables = {
  id: string;
  request: AddSecondaryOwnerRequest;
};

export function useAddBusinessOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'businessOwners', 'add'],
    mutationFn: ({ id, request }: AddBusinessOwnerVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/addBusinessOwner`, httpPost(request)),
    async onSuccess(_data, variables) {
      const id = variables.id;

      // A settings request that was already running would otherwise answer after this write and put the old owners back.
      await queryClient.cancelQueries({ queryKey: appQueryKeys.getAppSettings(id) });
      queryClient.setQueryData(appQueryKeys.getAppSettings(id), (settings) => {
        if (!settings) return settings;
        // The owner can already be listed when the request is repeated or a refetch brought it in.
        if (settings.businessOwnershipSettings.owners.some((owner) => sameId(owner.id, variables.request.owner.id))) {
          return settings;
        }

        return {
          ...settings,
          businessOwnershipSettings: {
            ...settings.businessOwnershipSettings,
            owners: [...settings.businessOwnershipSettings.owners, variables.request.owner],
          },
        };
      });

      if (sameId(Auth.getInstance().getUserAccount()?.id, variables.request.owner.id)) {
        queryClient.invalidateQueries({
          queryKey: appQueryKeys.getPermissions(variables.id),
        });

        await queryClient.cancelQueries({ queryKey: appQueryKeys.getMySettings });
        queryClient.setQueryData(appQueryKeys.getMySettings, (mySettings) => {
          if (!mySettings) return undefined;

          return {
            ...mySettings,
            businessOwnershipSettings: {
              ownerApps: {
                ...mySettings.businessOwnershipSettings.ownerApps,
                [id]: {
                  appType: variables.request.appType,
                },
              },
            },
          };
        });
      }
    },
  });
}

export type RemoveBusinessOwnerVariables = {
  id: string;
  businessOwnerId: string;
};

export function useRemoveBusinessOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'businessOwners', 'remove'],
    mutationFn: ({ id, businessOwnerId }: RemoveBusinessOwnerVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/removeBusinessOwner/${businessOwnerId}`, httpPost()),
    async onSuccess(_data, variables) {
      const id = variables.id;

      // A settings request that was already running would otherwise answer after this write and put the old owners back.
      await queryClient.cancelQueries({ queryKey: appQueryKeys.getAppSettings(id) });
      queryClient.setQueryData(
        appQueryKeys.getAppSettings(id),
        (settings) =>
          settings && {
            ...settings,
            businessOwnershipSettings: {
              ...settings.businessOwnershipSettings,
              owners: settings.businessOwnershipSettings.owners.filter(
                (businessOwner) => !sameId(businessOwner.id, variables.businessOwnerId)
              ),
            },
          }
      );

      if (sameId(Auth.getInstance().getUserAccount()?.id, variables.businessOwnerId)) {
        queryClient.invalidateQueries({
          queryKey: appQueryKeys.getPermissions(variables.id),
        });

        await queryClient.cancelQueries({ queryKey: appQueryKeys.getMySettings });
        queryClient.setQueryData(appQueryKeys.getMySettings, (mySettings) => {
          if (!mySettings) return undefined;

          if (!(id in mySettings.businessOwnershipSettings.ownerApps)) return mySettings;

          const { [id]: _removed, ...ownerApps } = mySettings.businessOwnershipSettings.ownerApps;

          return { ...mySettings, businessOwnershipSettings: { ...mySettings.businessOwnershipSettings, ownerApps } };
        });
      }
    },
  });
}

export type ExtendAppActivityVariables = {
  id: string;
};

export function useExtendAppActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['app', 'activity', 'extend'],
    mutationFn: ({ id }: ExtendAppActivityVariables) =>
      makeRequest(baseUrl, apiScopes, `apps/${id}/activity/reset`, httpPost(), toJson<PolicyExtension>),
    async onSuccess(data, variables) {
      const id = variables.id;

      // A settings request that was already running would otherwise answer after this write and put the old policy back.
      await queryClient.cancelQueries({ queryKey: appQueryKeys.getAppSettings(id) });
      queryClient.setQueryData(
        appQueryKeys.getAppSettings(id),
        (settings) =>
          settings && {
            ...settings,
            policy: data,
          }
      );
    },
  });
}

/**
 * The credentials of `source` on the cached `app`, which keeps every other field it has. The credential hooks write
 * with this after their `await`, not the app they read before it: a refetch that finished in between keeps its other
 * fields.
 */
function withCredentialsFrom(app: AppBaseDto, source: AppBaseDto): AppBaseDto {
  return { ...app, keyCredentials: source.keyCredentials, passwordCredentials: source.passwordCredentials };
}

/**
 * Carries the credentials of the detail app, after `updateCredentials` has settled them, onto a listed app.
 * A list read never returns key material or secret text, so a listed app gets neither: the detail app can hold
 * both right after an upload, which echoes the private key and its password.
 */
function withCredentialsOf(listed: AppBaseDto, app: AppBaseDto): AppBaseDto {
  return {
    ...listed,
    keyCredentials: app.keyCredentials.map((credential) => ({ ...credential, key: undefined })),
    passwordCredentials: app.passwordCredentials.map((credential) => ({ ...credential, secretText: undefined })),
  };
}

function getPagedQueryKeys(resourceType: ResourceType) {
  return resourceType === ResourceType.AppRegistration
    ? [graphQueryKeys.getMyAppRegistrations, graphQueryKeys.getAppRegistrations]
    : [graphQueryKeys.getMyEnterpriseApplications, graphQueryKeys.getEnterpriseApplications];
}

/**
 * Cancels the running requests of the entries that already hold data, so one that started before a write cannot
 * answer after it and put the old data back. An entry without data is left running: cancelling its first request
 * would revert it to pending with nothing to restart it, and the writers skip it anyway.
 * The match is exact: a prefix match on a paged list key would also catch the entry of every single app under it.
 */
async function cancelQueriesWithData(queryClient: QueryClient, queryKeys: readonly (readonly unknown[])[]) {
  await Promise.all(
    queryKeys.map((queryKey) =>
      queryClient.cancelQueries({ queryKey, exact: true, predicate: (query) => query.state.data !== undefined })
    )
  );
}

/** The resource entry of the app and, with `paged`, the paged lists `updateAppInPagedQueries` writes for its type. */
async function cancelAppQueries(
  queryClient: QueryClient,
  resourceType: ResourceType,
  id: string,
  { paged }: { paged: boolean }
) {
  await cancelQueriesWithData(queryClient, [
    graphQueryKeys.getResource(resourceType, id),
    ...(paged ? getPagedQueryKeys(resourceType) : []),
  ]);
}

function updateAppInPagedQueries(
  queryClient: QueryClient,
  resourceType: ResourceType,
  id: string,
  updater: (app: AppBaseDto) => AppBaseDto
) {
  for (const queryKey of getPagedQueryKeys(resourceType)) {
    queryClient.setQueryData(
      queryKey,
      (data: InfiniteData<GraphListResponse<AppBaseDto>, string | undefined> | undefined) => {
        if (!data) return data;

        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            value: (page.value || []).map((app) => (app.id === id ? updater(app) : app)),
          })),
        };
      }
    );
  }
}
